import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal, ConfirmDialog } from "../components/ui/Modal";
import { Input, Select } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { GiftBroadcastLevel } from "../api/types";

// Round 39 points 3/4 (migration 0091) — the global/all-room gift
// banner's "tier" catalog. coins.js's handleSendGift looks up, server-
// side, the highest ACTIVE level whose min_value is <= the REAL confirmed
// gift value (coins_cost * quantity actually paid — never a client-sent
// amount) and uses that level's own banner_animation_url for the gift
// banner, falling back to the gift's own animation_url when no level
// qualifies. A level has no per-gift link at all — it's computed
// dynamically from value every time a gift is sent, so editing a
// threshold here changes behavior immediately for every gift, with no
// gifts-table change needed.
//
// Round 45 item 1 fix — this table is now also the PRIMARY GATE for
// whether the Global Gift Banner fires at all, not just which animation
// it uses: any gift send whose confirmed total value reaches an active
// row's min_value here now triggers the banner automatically, with no
// per-gift toggle required. Lowering a threshold here (or adding a new
// low-value active row) makes MORE gift sends broadcast immediately;
// disabling every row here (is_active=false) stops automatic
// broadcasting entirely, leaving only gifts with Server Broadcast forced
// on in the Gift Catalog page.
export function GiftBroadcastLevelsPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } =
    usePagedList<GiftBroadcastLevel>("/admin/gift-broadcast-levels");
  const [editing, setEditing] = useState<Partial<GiftBroadcastLevel> | null>(null);
  const [pendingDelete, setPendingDelete] = useState<GiftBroadcastLevel | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  async function saveLevel() {
    if (!editing?.level || editing.min_value === undefined || editing.min_value === null) {
      showToast("Level and minimum value are required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const body = {
        level: editing.level,
        min_value: editing.min_value,
        banner_animation_url: editing.banner_animation_url || null,
        sort_order: editing.sort_order ?? 0,
        is_active: editing.is_active ?? true,
      };
      if (editing.id) {
        await apiRequest(`/admin/gift-broadcast-levels/${editing.id}`, { method: "PATCH", body });
      } else {
        await apiRequest("/admin/gift-broadcast-levels", { method: "POST", body });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save level", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/gift-broadcast-levels/${pendingDelete.id}`, { method: "DELETE" });
      showToast("Deleted", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete level", "error");
    } finally {
      setIsSubmitting(false);
      setPendingDelete(null);
    }
  }

  const columns: Column<GiftBroadcastLevel>[] = [
    { header: "Level", render: (row) => <span className="font-medium text-ink">Level {row.level}</span> },
    { header: "Min value", render: (row) => `${row.min_value.toLocaleString()} coins` },
    {
      header: "Banner animation",
      render: (row) =>
        row.banner_animation_url ? <Badge tone="gold">.svga set</Badge> : <span className="text-xs text-ink-faint">falls back to gift's own</span>,
    },
    { header: "Sort Order", render: (row) => row.sort_order },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>) },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>
            Edit
          </Button>
          <Button size="sm" variant="danger" onClick={() => setPendingDelete(row)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Gift Broadcast Levels">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        When a gift's real, server-confirmed total value (coins cost × quantity actually paid) meets or exceeds a
        level's minimum value, that level's banner animation plays on the global/all-room gift banner instead of the
        gift's own base animation. The highest qualifying active level always wins. A send that doesn't reach any
        level's minimum just uses the gift's own animation — nothing here is required before gifts work.
      </p>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEditing({})}>+ Add Level</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No broadcast levels yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Level" : "Add Level"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={saveLevel} isLoading={isSubmitting}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Level"
            type="number"
            value={editing?.level ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, level: Number(e.target.value) }))}
          />
          <Input
            label="Minimum value (coins)"
            type="number"
            value={editing?.min_value ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, min_value: Number(e.target.value) }))}
            hint="The confirmed gift total (coins cost × quantity actually paid) must be at least this much to qualify"
          />
          <Input
            label="Banner animation URL (.svga, optional)"
            value={editing?.banner_animation_url ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, banner_animation_url: e.target.value }))}
            hint="Leave blank to fall back to the gift's own animation when this level qualifies"
          />
          <Input
            label="Sort order"
            type="number"
            value={editing?.sort_order ?? 0}
            onChange={(e) => setEditing((p) => ({ ...p, sort_order: Number(e.target.value) }))}
          />
          <Select
            label="Status"
            value={(editing?.is_active ?? true) ? "yes" : "no"}
            onChange={(e) => setEditing((p) => ({ ...p, is_active: e.target.value === "yes" }))}
          >
            <option value="yes">Active</option>
            <option value="no">Inactive</option>
          </Select>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        isLoading={isSubmitting}
        isDangerous
        title={`Delete "Level ${pendingDelete?.level}"?`}
        description="A gift send that would have qualified for this level simply falls back to the next-highest qualifying level, or the gift's own animation if none qualify."
        confirmLabel="Delete"
      />
    </AppShell>
  );
}
